import React, { useEffect, useState } from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import Seo from '../components/Seo';
import { useTranslation } from '../i18n';
import {
  RentalConsultationFormData,
  RentalConsultationFormErrors,
  initialRentalConsultationFormData,
} from '../types/RentalConsultationForm';
import {
  buildRentalConsultationEmailBody,
  buildRentalConsultationEmailSubject,
  buildRentalConsultationMailtoLink,
  clearRentalFormDraft,
  generateRentalConsultationSummary,
  loadRentalFormDraft,
  saveRentalFormDraft,
  validateRentalConsultationForm,
} from '../utils/rentalFormUtils';

const DESIGN_TEAM_EMAIL = 'delythug@gmail.com';

interface PreparedEmailDraft {
  mailtoLink: string;
  subject: string;
  body: string;
  summary: string;
}

const RentalConsultation: React.FC = () => {
  const { locale, raw, t } = useTranslation();
  const [formData, setFormData] = useState<RentalConsultationFormData>(initialRentalConsultationFormData);
  const [errors, setErrors] = useState<RentalConsultationFormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasLoadedDraft, setHasLoadedDraft] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [preparedEmailDraft, setPreparedEmailDraft] = useState<PreparedEmailDraft | null>(null);
  const styleOptions = Object.entries(
    raw<Record<string, string>>('formOptions.dressSilhouettes')
  );
  const rentalPeriodOptions = Object.entries(
    raw<Record<string, string>>('formOptions.rentalPeriod')
  );
  const rentalBudgetOptions = Object.entries(
    raw<Record<string, string>>('formOptions.rentalBudget')
  );
  const howDidYouHearOptions = Object.entries(
    raw<Record<string, string>>('formOptions.howDidYouHear')
  );
  const services = raw<string[]>('rentalConsultation.services');
  const rentalTerms = raw<string[]>('rentalConsultation.terms');
  const captureItems = raw<string[]>('rentalConsultation.captureItems');

  useEffect(() => {
    const savedDraft = loadRentalFormDraft();

    if (savedDraft) {
      setFormData({
        ...initialRentalConsultationFormData,
        ...savedDraft,
      });
    }

    setHasLoadedDraft(true);
  }, []);

  useEffect(() => {
    if (!hasLoadedDraft) {
      return;
    }

    saveRentalFormDraft(formData);
  }, [formData, hasLoadedDraft]);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const navbarHeight = 80;
      const elementPosition = element.offsetTop - navbarHeight;
      window.scrollTo({
        top: elementPosition,
        behavior: 'smooth'
      });
    }
  };

  const handleInputChange = (field: keyof RentalConsultationFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    setErrors(prev => ({
      ...prev,
      [field]: undefined,
    }));
  };

  const handleCheckboxChange = (field: 'preferredStyles', value: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: checked 
        ? [...prev[field], value]
        : prev[field].filter(item => item !== value)
    }));
  };

  const handleCopyEmailBrief = async () => {
    if (!preparedEmailDraft) {
      return;
    }

    try {
      await navigator.clipboard.writeText(preparedEmailDraft.body);
      setStatusMessage(t('rentalConsultation.status.clipboardCopied'));
    } catch (error) {
      console.error('Clipboard copy error:', error);
      setStatusMessage(t('rentalConsultation.status.clipboardUnavailable'));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatusMessage(null);

    const validationErrors = validateRentalConsultationForm(formData, locale);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setStatusMessage(t('rentalConsultation.status.validationError'));
      return;
    }

    setIsSubmitting(true);

    try {
      const subject = buildRentalConsultationEmailSubject(formData, locale);
      const body = buildRentalConsultationEmailBody(formData, locale);
      const summary = generateRentalConsultationSummary(formData, locale);
      const mailtoLink = buildRentalConsultationMailtoLink(
        formData,
        DESIGN_TEAM_EMAIL,
        locale
      );

      setPreparedEmailDraft({
        mailtoLink,
        subject,
        body,
        summary,
      });

      window.location.href = mailtoLink;
      clearRentalFormDraft();
      setFormData(initialRentalConsultationFormData);
      setErrors({});
      setStatusMessage(t('rentalConsultation.status.success'));
    } catch (error) {
      console.error('Form submission error:', error);
      setStatusMessage(t('rentalConsultation.status.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f3ece3]">
      <Seo
        pageTitle={t('seo.rentalTitle')}
        description={t('seo.rentalDescription')}
      />
      <Navigation scrollToSection={scrollToSection} />
      
      {/* Rental Consultation Section */}
      <section className="min-h-screen bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.7),transparent_30%),radial-gradient(circle_at_bottom_right,rgba(214,189,159,0.14),transparent_28%)] pt-6 pb-16">
        <div className="container mx-auto px-4 py-10">
          <div className="mx-auto max-w-5xl rounded-[2rem] border border-white/70 bg-[rgba(255,250,244,0.72)] p-4 shadow-[0_28px_90px_rgba(117,95,74,0.12)] backdrop-blur-xl sm:p-6">
            <div className="mx-auto max-w-4xl">
              <h1 className="mb-6 text-center text-4xl font-light tracking-[0.14em] text-stone-800">
                {t('rentalConsultation.title')}
              </h1>
              <p className="mb-10 text-center text-lg leading-8 text-stone-700">
                {t('rentalConsultation.subtitle')}
              </p>

              <div className="mb-8 rounded-[1.75rem] border border-[#eadbc8] bg-[#fffdf9] p-6 shadow-[0_24px_60px_rgba(117,95,74,0.10)] sm:p-8">
                <div className="grid items-start gap-8 md:grid-cols-[1.4fr_1fr]">
                  <div>
                    <p className="mb-3 text-sm font-semibold uppercase tracking-[0.2em] text-stone-600">
                      {t('rentalConsultation.eyebrow')}
                    </p>
                    <h2 className="mb-4 text-3xl font-light tracking-[0.06em] text-stone-900">
                      {t('rentalConsultation.heroTitle')}
                    </h2>
                    <p className="mb-6 text-stone-700">
                      {t('rentalConsultation.heroDescription')}
                    </p>
                    <div className="flex flex-col gap-4 sm:flex-row">
                      <button
                        type="button"
                        onClick={() => scrollToSection('rental-form')}
                        className="inline-flex w-full items-center justify-center rounded-full bg-[#2f241d] px-8 py-4 text-center text-[11px] font-semibold uppercase tracking-[0.24em] text-white transition duration-300 hover:bg-[#43342a] sm:w-auto"
                      >
                        {t('rentalConsultation.submitButton')}
                      </button>
                      <a
                        href={`mailto:${DESIGN_TEAM_EMAIL}`}
                        className="inline-flex w-full items-center justify-center rounded-full border border-[#d8c7b2] bg-[#fffaf4]/82 px-8 py-4 text-center text-[11px] font-semibold uppercase tracking-[0.24em] text-stone-800 backdrop-blur-sm transition duration-300 hover:border-[#bea07a] hover:bg-[#fffdf9] sm:w-auto"
                      >
                        {t('rentalConsultation.contactTeamButton')}
                      </a>
                    </div>
                  </div>

                  <div className="rounded-[1.35rem] border border-[#efe1d2] bg-[#fffaf4] p-6 shadow-[0_12px_35px_rgba(117,95,74,0.08)]">
                    <h3 className="mb-4 text-lg font-semibold text-stone-900">{t('rentalConsultation.captureTitle')}</h3>
                    <ul className="space-y-3 text-sm text-stone-700">
                      {captureItems.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              {statusMessage && (
                <div
                  className={`rounded-xl px-5 py-4 mb-6 text-sm ${
                    preparedEmailDraft
                      ? 'bg-green-50 border border-green-200 text-green-800'
                      : 'bg-red-50 border border-red-200 text-red-800'
                  }`}
                  aria-live="polite"
                >
                  {statusMessage}
                </div>
              )}

              {preparedEmailDraft && (
                <div className="mb-8 rounded-[1.5rem] border border-[#eadbc8] bg-[#fffdf9] p-6 shadow-[0_20px_50px_rgba(117,95,74,0.10)]">
                  <div className="mb-4 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                    <div>
                      <h2 className="mb-2 text-2xl font-semibold text-stone-900">{t('rentalConsultation.preparedTitle')}</h2>
                      <p className="text-stone-700">
                        {t('rentalConsultation.preparedDescription')}
                      </p>
                    </div>
                    <div className="flex flex-col gap-3 sm:flex-row">
                      <a
                        href={preparedEmailDraft.mailtoLink}
                        className="inline-flex items-center justify-center rounded-full bg-[#2f241d] px-7 py-3.5 text-center text-[11px] font-semibold uppercase tracking-[0.24em] text-white transition duration-300 hover:bg-[#43342a]"
                      >
                        {t('rentalConsultation.openDraftAgain')}
                      </a>
                      <button
                        type="button"
                        onClick={handleCopyEmailBrief}
                        className="inline-flex items-center justify-center rounded-full border border-[#d8c7b2] bg-[#fffaf4]/82 px-7 py-3.5 text-[11px] font-semibold uppercase tracking-[0.24em] text-stone-800 backdrop-blur-sm transition duration-300 hover:border-[#bea07a] hover:bg-[#fffdf9]"
                      >
                        {t('rentalConsultation.copyEmailBrief')}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <p className="mb-2 text-sm font-semibold text-stone-700">{t('rentalConsultation.subject')}</p>
                      <div className="rounded-[1rem] border border-[#efe1d2] bg-[#fffaf4] px-4 py-3 text-sm text-stone-800">
                        {preparedEmailDraft.subject}
                      </div>
                    </div>
                    <div>
                      <p className="mb-2 text-sm font-semibold text-stone-700">{t('rentalConsultation.clientSummary')}</p>
                      <pre className="whitespace-pre-wrap rounded-[1rem] border border-[#efe1d2] bg-[#fffaf4] p-4 text-sm text-stone-800">
                        {preparedEmailDraft.summary}
                      </pre>
                    </div>
                  </div>
                </div>
              )}
              
              <form id="rental-form" onSubmit={handleSubmit} className="space-y-6">
              {/* Personal Information */}
              <div className="overflow-hidden rounded-[1.35rem] border border-[#eadbc8] bg-white p-6 shadow-[0_16px_45px_rgba(117,95,74,0.08)]">
                <h2 className="mb-5 border-b border-[#efe1d2] pb-4 text-2xl font-semibold text-stone-800">{t('rentalConsultation.sections.personalInformation')}</h2>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="firstName" className="mb-2 block text-sm font-medium text-stone-700">
                      {t('rentalConsultation.fields.firstName')}
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      required
                      value={formData.firstName}
                      onChange={(e) => handleInputChange('firstName', e.target.value)}
                      className={`w-full rounded-xl border bg-white/60 px-4 py-2 text-stone-800 placeholder:text-stone-400 shadow-sm outline-none backdrop-blur-sm transition focus:ring-2 focus:ring-[#bea07a] ${
                        errors.firstName ? 'border-red-400' : 'border-[#d8c7b2]'
                      }`}
                    />
                    {errors.firstName && <p className="mt-2 text-sm text-red-600">{errors.firstName}</p>}
                  </div>
                  <div>
                    <label htmlFor="lastName" className="mb-2 block text-sm font-medium text-stone-700">
                      {t('rentalConsultation.fields.lastName')}
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      required
                      value={formData.lastName}
                      onChange={(e) => handleInputChange('lastName', e.target.value)}
                      className={`w-full rounded-xl border bg-white/60 px-4 py-2 text-stone-800 placeholder:text-stone-400 shadow-sm outline-none backdrop-blur-sm transition focus:ring-2 focus:ring-[#bea07a] ${
                        errors.lastName ? 'border-red-400' : 'border-[#d8c7b2]'
                      }`}
                    />
                    {errors.lastName && <p className="mt-2 text-sm text-red-600">{errors.lastName}</p>}
                  </div>
                  <div>
                    <label htmlFor="email" className="mb-2 block text-sm font-medium text-stone-700">
                      {t('rentalConsultation.fields.email')}
                    </label>
                    <input
                      type="email"
                      id="email"
                      required
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      className={`w-full rounded-xl border bg-white/60 px-4 py-2 text-stone-800 placeholder:text-stone-400 shadow-sm outline-none backdrop-blur-sm transition focus:ring-2 focus:ring-[#bea07a] ${
                        errors.email ? 'border-red-400' : 'border-[#d8c7b2]'
                      }`}
                    />
                    {errors.email && <p className="mt-2 text-sm text-red-600">{errors.email}</p>}
                  </div>
                  <div>
                    <label htmlFor="phone" className="mb-2 block text-sm font-medium text-stone-700">
                      {t('rentalConsultation.fields.phone')}
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      required
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      className={`w-full rounded-xl border bg-white/60 px-4 py-2 text-stone-800 placeholder:text-stone-400 shadow-sm outline-none backdrop-blur-sm transition focus:ring-2 focus:ring-[#bea07a] ${
                        errors.phone ? 'border-red-400' : 'border-[#d8c7b2]'
                      }`}
                    />
                    {errors.phone && <p className="mt-2 text-sm text-red-600">{errors.phone}</p>}
                  </div>
                </div>
              </div>

              {/* Wedding Details */}
              <div className="overflow-hidden rounded-[1.35rem] border border-[#eadbc8] bg-white p-6 shadow-[0_16px_45px_rgba(117,95,74,0.08)]">
                <h2 className="mb-5 border-b border-[#efe1d2] pb-4 text-2xl font-semibold text-stone-800">{t('rentalConsultation.sections.weddingDetails')}</h2>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="weddingDate" className="mb-2 block text-sm font-medium text-stone-700">
                      {t('rentalConsultation.fields.weddingDate')}
                    </label>
                    <input
                      type="date"
                      id="weddingDate"
                      required
                      value={formData.weddingDate}
                      onChange={(e) => handleInputChange('weddingDate', e.target.value)}
                      className="w-full rounded-xl border border-[#d8c7b2] bg-white/60 px-4 py-2 text-stone-800 shadow-sm outline-none backdrop-blur-sm transition focus:ring-2 focus:ring-[#bea07a]"
                    />
                  </div>
                  <div>
                    <label htmlFor="venue" className="mb-2 block text-sm font-medium text-stone-700">
                      {t('rentalConsultation.fields.venue')}
                    </label>
                    <input
                      type="text"
                      id="venue"
                      value={formData.venue}
                      onChange={(e) => handleInputChange('venue', e.target.value)}
                      className="w-full rounded-xl border border-[#d8c7b2] bg-white/60 px-4 py-2 text-stone-800 shadow-sm outline-none backdrop-blur-sm transition focus:ring-2 focus:ring-[#bea07a]"
                    />
                  </div>
                  <div>
                    <label htmlFor="preferredRentalPeriod" className="mb-2 block text-sm font-medium text-stone-700">
                      {t('rentalConsultation.fields.preferredRentalPeriod')}
                    </label>
                    <select
                      id="preferredRentalPeriod"
                      value={formData.preferredRentalPeriod}
                      onChange={(e) => handleInputChange('preferredRentalPeriod', e.target.value)}
                      className="w-full rounded-xl border border-[#d8c7b2] bg-white/60 px-4 py-2 text-stone-800 shadow-sm outline-none backdrop-blur-sm transition focus:ring-2 focus:ring-[#bea07a]"
                    >
                      <option value="">{t('rentalConsultation.placeholders.selectRentalPeriod')}</option>
                      {rentalPeriodOptions.map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="budget" className="mb-2 block text-sm font-medium text-stone-700">
                      {t('rentalConsultation.fields.budget')}
                    </label>
                    <select
                      id="budget"
                      value={formData.budget}
                      onChange={(e) => handleInputChange('budget', e.target.value)}
                      className="w-full rounded-xl border border-[#d8c7b2] bg-white/60 px-4 py-2 text-stone-800 shadow-sm outline-none backdrop-blur-sm transition focus:ring-2 focus:ring-[#bea07a]"
                    >
                      <option value="">{t('rentalConsultation.placeholders.selectBudget')}</option>
                      {rentalBudgetOptions.map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Style Preferences */}
              <div className="overflow-hidden rounded-[1.35rem] border border-[#eadbc8] bg-white p-6 shadow-[0_16px_45px_rgba(117,95,74,0.08)]">
                <h2 className="mb-5 border-b border-[#efe1d2] pb-4 text-2xl font-semibold text-stone-800">{t('rentalConsultation.sections.stylePreferences')}</h2>
                <div className="space-y-4">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-stone-700">
                      {t('rentalConsultation.fields.preferredStyles')}
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      {styleOptions.map(([value, label]) => (
                        <label key={value} className="flex items-center">
                          <input
                            type="checkbox"
                            value={value}
                            checked={formData.preferredStyles.includes(value)}
                            onChange={(e) => handleCheckboxChange('preferredStyles', value, e.target.checked)}
                            className="mr-2 text-[#2f241d] focus:ring-[#bea07a]"
                          />
                          <span className="text-sm text-stone-700">{label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label htmlFor="dressSize" className="mb-2 block text-sm font-medium text-stone-700">
                      {t('rentalConsultation.fields.dressSize')}
                    </label>
                    <select
                      id="dressSize"
                      value={formData.dressSize}
                      onChange={(e) => handleInputChange('dressSize', e.target.value)}
                      className="w-full rounded-xl border border-[#d8c7b2] bg-white/60 px-4 py-2 text-stone-800 shadow-sm outline-none backdrop-blur-sm transition focus:ring-2 focus:ring-[#bea07a]"
                    >
                      <option value="">{t('rentalConsultation.placeholders.selectSize')}</option>
                      {Array.from({length: 20}, (_, i) => i + 0).map(size => (
                        <option key={size} value={size.toString()}>{size}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Additional Information */}
              <div className="overflow-hidden rounded-[1.35rem] border border-[#eadbc8] bg-white p-6 shadow-[0_16px_45px_rgba(117,95,74,0.08)]">
                <h2 className="mb-5 border-b border-[#efe1d2] pb-4 text-2xl font-semibold text-stone-800">{t('rentalConsultation.sections.additionalInformation')}</h2>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="specialRequests" className="mb-2 block text-sm font-medium text-stone-700">
                      {t('rentalConsultation.fields.specialRequests')}
                    </label>
                    <textarea
                      id="specialRequests"
                      rows={4}
                      value={formData.specialRequests}
                      onChange={(e) => handleInputChange('specialRequests', e.target.value)}
                      className="w-full rounded-xl border border-[#d8c7b2] bg-white/60 px-4 py-2 text-stone-800 placeholder:text-stone-400 shadow-sm outline-none backdrop-blur-sm transition focus:ring-2 focus:ring-[#bea07a]"
                      placeholder={t('rentalConsultation.placeholders.specialRequests')}
                    ></textarea>
                  </div>
                  <div>
                    <label htmlFor="howDidYouHear" className="mb-2 block text-sm font-medium text-stone-700">
                      {t('rentalConsultation.fields.howDidYouHear')}
                    </label>
                    <select
                      id="howDidYouHear"
                      value={formData.howDidYouHear}
                      onChange={(e) => handleInputChange('howDidYouHear', e.target.value)}
                      className="w-full rounded-xl border border-[#d8c7b2] bg-white/60 px-4 py-2 text-stone-800 shadow-sm outline-none backdrop-blur-sm transition focus:ring-2 focus:ring-[#bea07a]"
                    >
                      <option value="">{t('rentalConsultation.placeholders.selectOption')}</option>
                      {howDidYouHearOptions.map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Rental Information */}
              <div className="overflow-hidden rounded-[1.35rem] border border-[#eadbc8] bg-white p-6 shadow-[0_16px_45px_rgba(117,95,74,0.08)]">
                <h2 className="mb-5 border-b border-[#efe1d2] pb-4 text-2xl font-semibold text-stone-800">{t('rentalConsultation.sections.rentalInformation')}</h2>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="mb-3 text-lg font-semibold text-stone-800">{t('rentalConsultation.sections.servicesIncluded')}</h3>
                    <ul className="space-y-2 text-stone-700">
                      {services.map((service) => (
                        <li key={service} className="flex items-center">
                          <span className="mr-3 h-2 w-2 rounded-full bg-[#bea07a]"></span>
                          {service}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h3 className="mb-3 text-lg font-semibold text-stone-800">{t('rentalConsultation.sections.rentalTerms')}</h3>
                    <ul className="space-y-2 text-stone-700">
                      {rentalTerms.map((term) => (
                        <li key={term} className="flex items-center">
                          <span className="mr-3 h-2 w-2 rounded-full bg-[#bea07a]"></span>
                          {term}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="text-center">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`${
                    isSubmitting 
                      ? 'cursor-not-allowed bg-stone-400' 
                      : 'bg-[#2f241d] hover:bg-[#43342a]'
                  } inline-flex items-center justify-center rounded-full px-10 py-4 text-[11px] font-semibold uppercase tracking-[0.24em] text-white transition duration-300`}
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {t('common.submitting')}
                    </span>
                  ) : (
                    t('rentalConsultation.submitButton')
                  )}
                </button>
                <p className="mt-4 text-sm text-stone-600">
                  {t('rentalConsultation.submitHelper')}
                </p>
              </div>
            </form>
          </div>
        </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default RentalConsultation;


